import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, Send, Loader2, Sparkles, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { streamAi } from "@/lib/aiStream";
import { AiMarkdown } from "@/components/ai/AiMarkdown";

const STARTERS = [
    "How is PAYE worked out on my salary?",
    "How many days of annual leave am I entitled to?",
    "What does the 8% pension deduction cover?",
    "How should I request a salary advance?",
];

const Bubble = ({ m }) => (m.role === "user" ? (
    <div className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-sm bg-klein text-white px-4 py-2.5 text-sm whitespace-pre-wrap">{m.content}</div></div>
) : (
    <div className="flex gap-3">
        <div className="w-8 h-8 rounded-xl bg-terracotta-50 text-terracotta-500 flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1 rounded-2xl rounded-tl-sm bg-ink/[0.03] border border-ink/10 px-4 py-3">
            {m.content ? <AiMarkdown text={m.content} /> : <Loader2 className="w-4 h-4 animate-spin text-ink/40" />}
        </div>
    </div>
));

export const AiChat = () => {
    const [sessions, setSessions] = useState([]);
    const [sid, setSid] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);

    const loadSessions = useCallback(() => api.get("/ai/sessions").then(({ data }) => setSessions(data || [])).catch(() => {}), []);
    useEffect(() => { loadSessions(); }, [loadSessions]);
    useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

    const open = async (id) => {
        if (busy) return;
        setSid(id);
        if (!id) return setMsgs([]);
        const { data } = await api.get(`/ai/sessions/${id}/messages`).catch(() => ({ data: [] }));
        setMsgs(data);
    };

    const remove = async (id) => {
        await api.delete(`/ai/sessions/${id}`).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
        if (id === sid) open(null);
        loadSessions();
    };

    const send = async (text) => {
        const q = (text ?? input).trim();
        if (!q || busy) return;
        setInput("");
        setBusy(true);
        setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
        const append = (d) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], content: m[m.length - 1].content + d }]);
        try {
            await streamAi("/ai/chat", { session_id: sid, message: q }, { onSession: setSid, onDelta: append });
        } catch (e) {
            toast.error(e.message);
            setMsgs((m) => (m[m.length - 1].content ? m : m.slice(0, -1)));
        } finally {
            setBusy(false);
            loadSessions();
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4" data-testid="ai-chat">
            <aside className="bg-white border border-ink/10 p-3 lg:max-h-[70vh] overflow-y-auto" data-testid="ai-session-list">
                <button onClick={() => open(null)} className="w-full inline-flex items-center justify-center gap-2 bg-klein text-white px-4 py-2.5 text-sm hover:bg-klein/90 transition-colors" data-testid="ai-new-chat-btn">
                    <Plus className="w-4 h-4" /> New conversation
                </button>
                <div className="mt-3 space-y-1">
                    {sessions.length === 0 && <div className="py-6 text-center text-xs text-ink/40">No conversations yet</div>}
                    {sessions.map((s) => (
                        <div key={s.id} className={`group flex items-center gap-2 rounded-xl px-3 py-2 cursor-pointer transition-colors ${s.id === sid ? "bg-klein/10 text-klein" : "hover:bg-ink/5 text-ink/75"}`}
                            onClick={() => open(s.id)} data-testid={`ai-session-${s.id}`}>
                            <MessageSquare className="w-4 h-4 shrink-0" />
                            <span className="flex-1 truncate text-sm">{s.title}</span>
                            <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="opacity-0 group-hover:opacity-100 text-ink/40 hover:text-terracotta-500 transition-opacity" aria-label="Delete conversation" data-testid={`ai-session-delete-${s.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            </aside>
            <section className="bg-white border border-ink/10 flex flex-col min-h-[60vh] lg:h-[70vh]">
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="ai-thread">
                    {msgs.length === 0 && (
                        <div className="h-full flex flex-col items-start justify-center gap-4 max-w-xl" data-testid="ai-empty">
                            <div className="overline">Ask HR</div>
                            <h2 className="font-display font-bold text-2xl text-ink">What would you like to know?</h2>
                            <p className="text-sm text-ink/55">General answers about policies, leave, pay, PAYE and pensions in Nigeria. Check your own figures on the Payslips, Leave and Loans pages.</p>
                            <div className="flex flex-wrap gap-2">
                                {STARTERS.map((s, i) => (
                                    <button key={s} onClick={() => send(s)} className="rounded-full border border-ink/15 px-3.5 py-1.5 text-xs text-ink/70 hover:border-klein hover:text-klein transition-colors" data-testid={`ai-starter-${i}`}>{s}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    {msgs.map((m, i) => <Bubble key={i} m={m} />)}
                    <div ref={endRef} />
                </div>
                <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-ink/10 p-3 flex items-end gap-2">
                    <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} maxLength={4000} placeholder="Ask a question…"
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        className="flex-1 resize-none max-h-40 rounded-xl border border-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-klein" data-testid="ai-chat-input" />
                    <button type="submit" disabled={busy || !input.trim()} className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-xl bg-klein text-white disabled:opacity-50" aria-label="Send" data-testid="ai-chat-send-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </form>
            </section>
        </div>
    );
};

export default AiChat;
